import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { CompanyStore } from "../models/company.store.model";
import { localized } from "./SwitchLanguage";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import CompanyStoreGoogleMap from "./CompanyStoreGoogleMap";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { Survey } from "../models/survey.model";
import { debounce } from "lodash";
import { PaginationFilterType, Position } from "../models/base.model";

const filterOptions = createFilterOptions({
  stringify: (option: CompanyStore) => `${option.name} ${option.storeId} ${option.postCode} ${option.phoneNumber}`,
});

export default function CompanyStoreSelection({
  control,
  language,
  dataTestId,
  companyId,
  survey,
  onSelectStore,
}: {
  control: Control;
  language: string;
  dataTestId?: string;
  companyId: string;
  survey: Survey;
  onSelectStore: (store?: CompanyStore) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<CompanyStore[]>();
  const [currentPosition, setCurrentPosition] = useState<Position | undefined>();

  useEffect(() => {
    if (stores && stores.length > 0) {
      if (navigator.geolocation) {
        navigator.geolocation.watchPosition(
          (position: GeolocationPosition) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            setCurrentPosition(location);
          },
          () => {
            window.webApp.showMessage("error", "Failed to get location permission.");
          }
        );
      } else {
        // Browser doesn't support Geolocation
        window.webApp.showMessage("error", "Geolocation is not supported by this browser.");
      }
    }
  }, [stores]);

  const loadStores = useCallback(
    debounce(async (searchValue?: string) => {
      const fetchOnce = async (offset?: any) => {
        return await window
          .fetch(`${process.env.REACT_APP_API_URL}/company-stores/company/${companyId}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              limit: 100,
              offset,
              filter: searchValue
                ? {
                    or: ["name", "phoneNumber", "postCode", "storeId", "position"].map((k) => ({
                      key: k,
                      values: [searchValue.toUpperCase()],
                      type: PaginationFilterType.contains,
                    })),
                  }
                : undefined,
            }),
          })
          .then((d) => d.json());
      };

      setLoading(true);
      try {
        let offset = undefined;
        let allStores: CompanyStore[] = [];
        do {
          const res: any = await fetchOnce(offset);
          offset = res.offset;
          allStores = [...allStores, ...res.stores];
        } while (offset);
        setStores(allStores);
      } catch (error) {
        console.error("error", error);
      } finally {
        setLoading(false);
      }
    }, 500),
    []
  );
  useEffect(() => {
    loadStores();
  }, []);

  const sortedStores = useMemo(() => {
    if (stores && currentPosition) {
      return stores.sort(
        (a, b) =>
          (a.position ? getDistanceFromLatLng(currentPosition, a.position) : 0) -
          (b.position ? getDistanceFromLatLng(currentPosition, b.position) : 0)
      );
    } else {
      return stores?.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [stores, currentPosition]);

  return (
    <Controller
      name={"storeId"}
      control={control}
      render={({ field }) => (
        <Stack spacing={4} sx={{ width: "100%", height: "100%" }}>
          <Autocomplete
            {...field}
            options={sortedStores || []}
            loading={loading}
            autoHighlight
            getOptionLabel={(option) => option.name}
            onChange={(_, newValue) => {
              field.onChange(newValue ? newValue.id : null);
              onSelectStore(newValue || undefined);
            }}
            value={stores?.find((s) => s.id === field.value) || null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, { index }) => {
              const { ...optionProps } = props;

              return (
                <Box data-testid={`${dataTestId || "storeId"}-option${index}`} component="li" {...optionProps} key={option.id}>
                  <Stack direction={"column"} sx={{ width: "100%" }}>
                    <Box>
                      {option.name} {option.postCode ? `(${option.postCode})` : ""} - {option.storeId}
                    </Box>
                    {currentPosition && option.position && (
                      <Box sx={{ color: "gray", fontSize: 12 }}>
                        {formatDistance(getDistanceFromLatLng(currentPosition, option.position))}
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            }}
            renderInput={(props) => (
              <TextField
                {...props}
                label={localized(language, survey, "storeSearchPlaceholder", "Search for location")}
                autoComplete="off"
                inputProps={{
                  "data-testid": dataTestId || "storeId",
                  ...props.inputProps,
                  autoComplete: "off",
                }}
                InputProps={{
                  ...props.InputProps,
                  endAdornment: (
                    <Fragment>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {props.InputProps.endAdornment}
                    </Fragment>
                  ),
                }}
              />
            )}
            filterOptions={filterOptions}
          />
          {stores ? (
            <CompanyStoreGoogleMap
              language={language}
              stores={stores.filter((s) => s.position) || []}
              onSelectedStore={(store: CompanyStore) => {
                field.onChange(store.id);
              }}
            />
          ) : (
            <Stack justifyContent={"center"} alignItems={"center"} height={"100%"} width={"100%"}>
              <CircularProgress />
            </Stack>
          )}
        </Stack>
      )}
    />
  );
}

function getDistanceFromLatLng(position1: Position, position2: Position): number {
  const R = 6371e3; // Earth radius in meters
  const toRad = (value: number) => (value * Math.PI) / 180;

  const φ1 = toRad(position1.lat);
  const φ2 = toRad(position2.lat);
  const Δφ = toRad(position2.lat - position1.lat);
  const Δλ = toRad(position2.lng - position1.lng);

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // distance in meters
}

function formatDistance(distance: number): string {
  if (distance < 1000) {
    return distance.toFixed(2) + " meters";
  } else if (distance < 1000000) {
    return (distance / 1000).toFixed(2) + " km";
  } else {
    return "Too far";
  }
}
